'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';

interface UseSliderEntryEffectOptions {
  /** The Swiper instance (available after onSwiper fires). */
  swiper: SwiperType | null;
  /** Total number of items with entry effects. Used to short-circuit once all are revealed. */
  totalItems: number;
  /** Items that actually have entryEffect enabled, keyed by index. */
  effectIndices: Set<number>;
}

interface UseSliderEntryEffectReturn {
  /** Ref to attach to the slider's outermost wrapper for page-scroll detection. */
  containerRef: React.RefObject<HTMLElement | null>;
  /** Whether a given slide index has been revealed (entry effect should be in "visible" state). */
  isRevealed: (index: number) => boolean;
}

/**
 * Manages scroll-triggered entry effects for items inside a Swiper carousel.
 *
 * Standard IntersectionObserver on individual slides fails inside Swiper because
 * slide visibility changes are driven by `transform: translate3d()` on the parent
 * `.swiper-wrapper`, which doesn't reliably trigger IO callbacks on children.
 *
 * This hook solves the problem by combining:
 * 1. An IntersectionObserver on the **slider container** to detect when the slider
 *    enters the viewport via page scroll.
 * 2. Swiper's own `slideChangeTransitionEnd` event to detect newly visible slides
 *    after swiping.
 *
 * The Swiper instance is stored in a ref so the IO callback always reads the
 * latest value, avoiding the stale-closure race where the IO fires before
 * onSwiper has set the instance.
 */
export const useSliderEntryEffect = ({
  swiper,
  totalItems,
  effectIndices,
}: UseSliderEntryEffectOptions): UseSliderEntryEffectReturn => {
  const containerRef = useRef<HTMLElement>(null);
  const [revealedSlides, setRevealedSlides] = useState<Set<number>>(new Set());
  const inViewRef = useRef(false);
  const allRevealedRef = useRef(false);
  const swiperRef = useRef<SwiperType | null>(swiper);
  swiperRef.current = swiper;

  const revealVisible = useCallback(
    (swiperInst: SwiperType) => {
      if (allRevealedRef.current || !inViewRef.current) return;

      const perView = Math.ceil(
        typeof swiperInst.params.slidesPerView === 'number' ? swiperInst.params.slidesPerView : 1
      );
      const start = swiperInst.activeIndex;
      const end = Math.min(start + perView + 1, totalItems);

      setRevealedSlides((prev) => {
        const next = new Set(prev);
        for (let i = start; i < end; i++) {
          if (effectIndices.has(i)) next.add(i);
        }
        if (next.size >= effectIndices.size) allRevealedRef.current = true;
        return next.size === prev.size ? prev : next;
      });
    },
    [totalItems, effectIndices]
  );

  useEffect(() => {
    if (effectIndices.size === 0) return;

    const el = containerRef.current;
    if (!el) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setRevealedSlides(new Set(effectIndices));
      allRevealedRef.current = true;
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || inViewRef.current) return;
        inViewRef.current = true;
        observer.disconnect();
        if (swiperRef.current) revealVisible(swiperRef.current);
      },
      { threshold: 0.1 }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [effectIndices, revealVisible]);

  useEffect(() => {
    if (!swiper || effectIndices.size === 0) return;

    if (inViewRef.current) revealVisible(swiper);

    const handler = () => revealVisible(swiper);
    swiper.on('slideChangeTransitionEnd', handler);
    swiper.on('reachEnd', handler);

    return () => {
      swiper.off('slideChangeTransitionEnd', handler);
      swiper.off('reachEnd', handler);
    };
  }, [swiper, revealVisible, effectIndices]);

  const isRevealed = useCallback(
    (index: number) => !effectIndices.has(index) || revealedSlides.has(index),
    [effectIndices, revealedSlides]
  );

  return { containerRef, isRevealed };
};
